import axios from 'axios';
import { BaseHttpClient } from '../BaseHttpClient';
import { injectCommonInterceptors } from '../interceptors';
import {
  GetConfigResponse,
  UpdateConfigRequest,
  UpdateConfigResponse,
  UpdateRepoSettingsRequest,
  UpdateRepoSettingsResponse,
} from './api.schema';
import { BASE_URL } from './consts';

const axiosConfigInstance = axios.create({
  baseURL: BASE_URL,
});

injectCommonInterceptors(axiosConfigInstance);

export class ConfigClient extends BaseHttpClient {
  constructor() {
    super(axiosConfigInstance);
  }

  static override instance = new ConfigClient();

  /**
   * Get settings for current user's workspace.
   * When repo identity is provided, per-repo overrides are deep-merged on
   * top of the workspace blob so the response reflects the effective config
   * for that repo.
   * @param repo - Optional repo identity to merge overrides into the response
   * @returns Promise with config values
   */
  static async getSettings(repo?: { provider: string; fullName: string }) {
    if (!repo) {
      return await ConfigClient.get<GetConfigResponse>('/settings');
    }

    const params = new URLSearchParams({
      git_provider: repo.provider,
      repo_full_name: repo.fullName,
    });

    return await ConfigClient.get<GetConfigResponse>(
      `/settings?${params.toString()}`,
    );
  }

  /**
   * Update settings for current user's workspace
   * @param payload - The settings updates to apply
   * @returns Promise with success status and any validation errors
   */
  static async updateSettings(payload: UpdateConfigRequest) {
    return await ConfigClient.patch<UpdateConfigResponse>('/settings', payload);
  }

  /**
   * Reset portal-managed settings to defaults for current user's workspace.
   * Keys set by other services are preserved.
   * @returns Promise with success status and message
   */
  static async resetSettings() {
    return await ConfigClient.delete<UpdateConfigResponse>('/settings');
  }

  /**
   * Reset a repository's config overrides so it inherits the workspace value
   * (or default). Capability toggles (CR/CE) are preserved.
   * @param gitProvider - Git provider (e.g., 'github', 'gitlab')
   * @param repoFullName - Full repository name (e.g., 'owner/repo-name')
   * @returns Promise with success status and any validation errors
   */
  static async resetRepoSettings(gitProvider: string, repoFullName: string) {
    const params = new URLSearchParams({
      git_provider: gitProvider,
      repo_full_name: repoFullName,
    });

    return await ConfigClient.delete<UpdateConfigResponse>(
      `/repo-settings?${params.toString()}`,
    );
  }

  /**
   * Update repository-specific settings
   * @param gitProvider - Git provider (e.g., 'github', 'gitlab')
   * @param repoFullName - Full repository name (e.g., 'owner/repo-name')
   * @param payload - The repository settings updates to apply
   * @returns Promise with success status and any validation errors
   */
  static async updateRepoSettings(
    gitProvider: string,
    repoFullName: string,
    payload: UpdateRepoSettingsRequest,
  ) {
    const params = new URLSearchParams({
      git_provider: gitProvider,
      repo_full_name: repoFullName,
    });

    return await ConfigClient.patch<UpdateRepoSettingsResponse>(
      `/repo-settings?${params.toString()}`,
      payload,
    );
  }
}
